import { computed } from 'vue'

import useRef from '@nicolaudie-group/ng-ui-kit/composables/useRef'

import useRouterStore from '@/stores/router.store'

/**
 * @typedef RouteDestination
 * @type {
 *  import('vue-router').RouteLocationNamedRaw
 *  |import('vue').Ref<import('vue-router').RouteLocationNamedRaw>
 * }
 */

/**
 * @typedef UseDestinationResult
 * @type {import('vue').ComputedRef<import('vue-router').RouteLocationNamedRaw>}
 */

/**
 * Creates a computed object that can be used as destination of a RouterLink/PbLink.
 *
 * It implicitely copies all the parameters on the current route that the destination
 * also supports.
 *
 * @param {RouteDestination} to - Explicit destination data
 * @returns {import('vue-router').RouteLocationNamedRaw} -
 * Destination route object with implicit parameters
 */
function useDestination(to) {
  const destinationRef = useRef(to)
  const routerStore = useRouterStore()

  const implicitParams = computed(() => {
    // Find the destination route by it's name
    const destination = routerStore.resolve({
      ...destinationRef.value,
      params: {
        ...destinationRef.value.params,
      },
    })

    // List the params of the destination route
    const keys = Object.keys(destination.params)

    // Implicitely copy the params from the current route that are in the destination route
    const result = {}
    const { params } = routerStore.route
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i]
      const value = params[key]
      result[key] = value
    }
    return result
  })

  const destinationRoute = computed(() => ({
    // Copy the current route
    ...routerStore.route,
    // Apply the destination name and other properties
    ...destinationRef.value,
    params: {
      // Implicitely apply params from the current route
      ...implicitParams.value,
      // Apply the params defined explicitely on the destination
      ...destinationRef.value.params,
    },
  }))

  return destinationRoute
}

export default useDestination
